import { UTCDate } from "@date-fns/utc";
import { useMemo } from "react";
import {
  GraphBarProps,
  WeeklyDataPoint,
  WeeklyGraph,
} from "../common/WeeklyGraph";

type ActivityDataPoint = WeeklyDataPoint & {
  received: number;
  handled: number;
  success: number;
  disqualified: number;
  postponed: number;
  burned: number;
  dailyBurnRate: number;
  isScalingHelper?: boolean;
};

type Props = {
  periods: Array<{
    start: string;
    end: string;
    received: number;
    receivedAndHandled: number;
    success: number;
    disqualified: number;
    postponed: number;
    successRate: number;
    burned: number;
    date?: string;
  }>;
  isLoading?: boolean;
  alignment?: "center" | "left" | "right";
  baseDate?: UTCDate;
  minDate?: UTCDate;
  maxDate?: UTCDate;
  showBurnRateTrend?: boolean;
};

export function ActivitiesDashboardGraph({
  periods,
  isLoading,
  alignment,
  baseDate,
  minDate,
  maxDate,
  showBurnRateTrend = false,
}: Props) {
  const data = useMemo((): ActivityDataPoint[] => {
    if (!periods || periods.length === 0) return [];

    return periods.map((period) => {
      const dailyBurnRate = period.burned;

      return {
        date: new UTCDate(period.start),
        value: period.success + period.disqualified + period.postponed,
        received: period.received,
        handled: period.receivedAndHandled,
        success: period.success,
        disqualified: period.disqualified,
        postponed: period.postponed,
        burned: period.burned,
        dailyBurnRate,
      };
    });
  }, [periods]);

  const hasActivity = useMemo(() => {
    return data.some(
      (d) => d.success > 0 || d.disqualified > 0 || d.postponed > 0,
    );
  }, [data]);

  return (
    <div className="px-8 xl:px-14">
      <WeeklyGraph
        data={data}
        alignment={alignment}
        baseDate={baseDate}
        minDate={minDate}
        maxDate={maxDate}
        isLoading={isLoading}
        hasData={hasActivity}
        noDataText="Ingen aktivitet i den viste periode"
        showCurrentWeekCursor={true}
        showTrendLine={showBurnRateTrend}
        renderBar={(props) => <ActivityBar {...props} />}
      />
    </div>
  );
}

function ActivityBar({
  data,
  maxValue,
  index,
}: GraphBarProps<ActivityDataPoint> & {
  maxValue: number;
  index?: number;
}) {
  const d = data as ActivityDataPoint;

  const total = d.success + d.disqualified + d.postponed;
  const barPct = maxValue > 0 ? (total / maxValue) * 100 : 0;

  const segments = useMemo(
    () => [
      {
        key: "success" as const,
        count: d.success,
        className: "bg-green-600 dark:bg-green-400",
      },
      {
        key: "disqualified" as const,
        count: d.disqualified,
        className: "bg-theme-on-background-variant/80",
      },
      {
        key: "postponed" as const,
        count: d.postponed,
        className: "bg-theme-on-background-variant/50",
      },
    ],
    [d.success, d.disqualified, d.postponed],
  );

  let bottomAcc = 0;

  return (
    <div
      className="relative flex h-full w-full items-end justify-center"
      data-bar-week={index}
    >
      <div className="relative flex h-full w-3.5 items-end justify-center">
        <div
          className="relative w-full overflow-hidden transition-all"
          style={{ height: `${barPct}%` }}
        >
          {segments.map((s, i) => {
            if (total === 0 || s.count === 0) return null;

            const rawPct = (s.count / total) * 100;
            const heightPct =
              i === segments.length - 1 ? Math.max(0, 100 - bottomAcc) : rawPct;
            const el = (
              <div
                key={s.key}
                className={`${s.className} absolute w-full`}
                style={{ bottom: `${bottomAcc}%`, height: `${heightPct}%` }}
              />
            );
            bottomAcc += rawPct;
            return el;
          })}
        </div>
      </div>
    </div>
  );
}
